import React, { useContext, useRef, useState } from "react";
import { useIntl } from "react-intl";
import axios from "axios";
import ProgressBar from "./ProgressBar";
import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";
import { FormattedMessage } from "react-intl";
import { PlusIcon, PhotoIcon } from "@heroicons/react/24/solid";
import {ExclamationTriangleIcon, ScissorsIcon, TrashIcon} from "@heroicons/react/24/outline";
import { NotificationsContext } from "../ui-components/Notifications";
import CountryContext from "../CountryContext";
import WorksiteImageDescription from "./WorksiteImageDescription";
import Button from "../ui-components/Button";
import PrivateImage from "./PrivateImage";
import ImageCropper from "./ImageCropper";
import _ from "lodash";
import mime from "mime-types";
import {CROP_ASPECT_RATIO, CROP_ASPECT_RATIO_TOLERANCE} from "../utils/constants";

const re = /(?:\.([^.]+))?$/;
export const uploadImage = async (
  file,
  format,
  onUploadStart,
  onUploadProgress,
  onSafeEnd,
  existingImageUrl = null,
  apiRequest,
  prefix,
  bucket,
) => {

  // --- get signed url to be used to upload image to S3
  const {url, fields} = await apiRequest.post(`/aws-signed-url`, {
    content_type: file.type,
    format: format,
    prefix: prefix,
    // prefix: `images/${country.code}/worksites/${userId}`,  // won't be used if existingImageUrl is given
    // bucket: "worksite_images",
    bucket: bucket,
    method: "POST",
    existing_image_url: existingImageUrl
  });

  // --- prepare s3 upload request's payload
  const formData = new FormData();
  Object.keys(fields).map((key) => {
    return formData.append(key, fields[key]);
  });
  // Actual file has to be appended last.
  formData.append("file", file);

  // --- upload image to s3
  onUploadStart();
  const options = {
    headers: {
      "Content-Type": "multipart/form-data",
      "Access-Control-Allow-Origin": "*",
      "Cache-Control": "max-age=31536000",
    },
    onUploadProgress: onUploadProgress
  };
  try {
    await axios.post(url, formData, options);
    if (existingImageUrl === null)
      return {image_url: url + fields.key, des_image: "", loadedOn: new Date()}
  } catch (error) {
    console.log(error);
    push({
      title: "Something went wrong",
      type: "error",
    });
    return false;
  } finally {
    onSafeEnd();
  }
  return true;
}

export const checkUploadedImage = (file, maxSize) => {

  let error = null;
  const format = re.exec(file.name)[1];

  if (!format)
    error = file.name + " has unknown file format";

  if (maxSize) {
    const size = file.size;
    const kb = Math.round(size / 1024);
    if (maxSize < kb)
      error = file.name + " is too big";
  }

  return {format, error};
}

export const getImageSize = (url, callback) => {
  const img = new Image();
  img.src = url;
  img.onload = function () {
    callback(this.width, this.height);
  }
}

const WorksiteImageUploader = ({
  setImagesData,
  maxSize = 5120,
  imagesData,
  saveImages,
  userId,
}) => {
  const hiddenImageInputRef = useRef();

  const intl = useIntl();
  const { country } = useContext(CountryContext);
  const { push } = useContext(NotificationsContext);
  const apiRequest = useCountryAxiosInstance();

  const [progress, setProgress] = useState(0);
  const [signedUrls, setSignedUrls] = useState({});
  const [crops, setCrops] = useState({});
  const [cropUploadProgresses, setCropUploadProgresses] = useState({});
  const [nonCompliantImages, setNonCompliantImages] = useState(new Set());
  const [imagesBeingUploadedProgresses, setImagesBeingUploadedProgresses] = useState([]);



  // const checkUploadedImage = (file) => {
  //
  //   let error = null;
  //   const format = re.exec(file.name)[1];
  //
  //   if (!format)
  //     error = file.name + " has unknown file format";
  //
  //   if (maxSize) {
  //     const size = file.size;
  //     const kb = Math.round(size / 1024);
  //     if (maxSize < kb)
  //       error = file.name + " is too big";
  //   }
  //
  //   return { format, error };
  // }

  // const uploadImage = async (file, format, onUploadStart, onUploadProgress, onSafeEnd, existingImageUrl=null) => {
  //
  //   // --- get signed url to be used to upload image to S3
  //   const { url, fields } = await apiRequest.post(`/aws-signed-url`, {
  //     content_type: file.type,
  //     format: format,
  //     prefix: `images/${country.code}/worksites/${userId}`,  // won't be used if existingImageUrl is given
  //     bucket: "worksite_images",
  //     method: "POST",
  //     existing_image_url: existingImageUrl
  //   });
  //
  //   // --- prepare s3 upload request's payload
  //   const formData = new FormData();
  //   Object.keys(fields).map((key) => {
  //     return formData.append(key, fields[key]);
  //   });
  //   // Actual file has to be appended last.
  //   formData.append("file", file);
  //
  //   // --- upload image to s3
  //   onUploadStart();
  //   const options = {
  //     headers: {
  //       "Content-Type": "multipart/form-data",
  //       "Access-Control-Allow-Origin": "*",
  //       "Cache-Control": "max-age=31536000",
  //     },
  //     onUploadProgress: onUploadProgress
  //   };
  //   try {
  //     await axios.post(url, formData, options);
  //     if (existingImageUrl === null)
  //       return { image_url: url + fields.key, des_image: "", loadedOn: new Date() }
  //   }
  //   catch (error) {
  //     console.log(error);
  //     push({
  //       title: "Something went wrong",
  //       type: "error",
  //     });
  //     return false;
  //   }
  //   finally {
  //     onSafeEnd();
  //   }
  //   return true;
  // }

  const uploadCrop = async (imageUrl, file) => {
    // replace s3 image with crop
    const success = await uploadImage(
      file,
      re.exec(imageUrl).slice(-1)[0],
      () => {
        setCropUploadProgresses({...cropUploadProgresses, [imageUrl]: 0});
      },
      (progressEvent) => {
        const percentCompleted = Math.floor((progressEvent.loaded * 100) / progressEvent.total);
        setCropUploadProgresses({...cropUploadProgresses, [imageUrl]: percentCompleted});
      },
      () => {
        // remove current image url from crops in 'sending' state
        setCropUploadProgresses(_.pick(
          cropUploadProgresses,
          Object.keys(cropUploadProgresses).filter((k) => k !== imageUrl)
        ))
      },
      imageUrl,
      apiRequest,
      `images/${country.code}/worksites/${userId}`,
      "worksite_images",
    );

    if (success) {
      // close crop utility if everything went fine
      setCrops((prev) => _.pick(prev, Object.keys(prev).filter((k) => k !== imageUrl)))
      // remove target image from non-compliant ones
      setNonCompliantImages(new Set([...nonCompliantImages].filter((url) => url !== imageUrl)));
      // upload load timestamp on updated image in order to trigger its refresh
      setImagesData((prev) => prev.map((img) => img.image_url === imageUrl ? {...img, loadedOn: new Date()} : img))
    }
  }

  return (
    <div className="flex w-full flex-col">
      <div className="mx-8 my-6">
        <h3 className="text-center text-2xl font-extrabold text-black-500">
          <FormattedMessage id="worksite_images"/>
        </h3>
      </div>
      <div>

        {/* Hidden file uploader, triggered by fancy "new image" button */}
        <input
          multiple
          accept="image/jpeg,image/png,video/mp4"
          ref={hiddenImageInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={async (e) => {

            const imagesToUploadWithFormats = []
            for (let file of Object.values(e.target.files)) {
              const {format, error} = checkUploadedImage(file, maxSize);
              if (error) {
                push({title: error, type: "error"});
              } else {
                imagesToUploadWithFormats.push({file, format})
              }
            }
            const numberOfImagesAlreadyBeingUploaded = imagesBeingUploadedProgresses.length;
            setImagesBeingUploadedProgresses([...imagesBeingUploadedProgresses,
                ...(imagesToUploadWithFormats.map(({file, format}) => {
                    return {
                        file:file,
                        progress:0
                    }
                }))]);
            let imagesUploaded = []
            let index = 0
            for (let {file, format} of imagesToUploadWithFormats) {
              // upload file to S3
              let {image_url, des_image, loadedOn} = await uploadImage(
                  file,
                  format,
                  () => {
                    setProgress(0);
                  },
                  (progressEvent) => {
                    const progress = Math.floor((progressEvent.loaded * 100) / progressEvent.total)
                    setImagesBeingUploadedProgresses((imagesBeingUploadedProgresses) => {
                      let tmp = [...imagesBeingUploadedProgresses];
                      tmp[numberOfImagesAlreadyBeingUploaded + index].progress = progress;
                      return tmp;
                    });
                  },
                  () => {
                    setProgress(0);
                  },
                  null,
                  apiRequest,
                  `images/${country.code}/worksites/${userId}`,
                  "worksite_images",
              )
              index++
              imagesUploaded.push({image_url, des_image, loadedOn})
            }
            setImagesData([...imagesData, ...imagesUploaded])
            setImagesBeingUploadedProgresses([])
          }}
        />

        {/* ******************** EXISTING IMAGES ******************** */}
        {
          imagesData?.map(({ image_url, des_image, loadedOn }, index) => (
            <div key={index} className="my-9">
              {/* ==================== Image ==================== */}
              <div className={`flex items-center gap-4 ${image_url in crops ? 'opacity-30 pointer-events-none': ''}`}>
                <div className={`border-4 overflow-hidden w-36`} style={{aspectRatio: CROP_ASPECT_RATIO.toString()}}>
                  {/* -------------------- Preview -------------------- */}
                  <PrivateImage
                    image_url={image_url}
                    tag="div"
                    className="w-full h-full bg-cover bg-center"
                    onRetrievedSignedURL={(sUrl) => {
                      // collect the signed url into the state
                      setSignedUrls((prev) => ({...prev, [image_url]: sUrl}))
                      // mark image as non-compliant if its aspect ratio is different from the preferred
                      getImageSize(sUrl, (w, h) => {
                        if (Math.abs(w / h - CROP_ASPECT_RATIO) < CROP_ASPECT_RATIO_TOLERANCE)
                          return;
                        setNonCompliantImages((prev) =>new Set([...prev, image_url]));
                      })
                    }}
                    {...{loadedOn}}
                  />
                </div>
                <div className="flex-grow">
                  {/* -------------------- Controls -------------------- */}
                  {
                    nonCompliantImages.has(image_url) && (
                      <div className="flex items-center gap-1 text-xs text-yellow-500 my-1.5">
                        <ExclamationTriangleIcon className="w-3" />
                        <span>{intl.formatMessage({id: 'non_standard_image_size'})}</span>
                      </div>
                    )
                  }
                  <div className="w-full mb-3">
                    <WorksiteImageDescription
                      imagesData={imagesData}
                      setImagesData={setImagesData}
                      imageUrl={image_url}
                      desImage={des_image}
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 mb-1">
                    <div className="flex-grow">
                      <Button
                        full
                        styleType="default"
                        onClick={() => {
                          setCrops({...crops, [image_url]: undefined})
                        }}
                        disabled={!signedUrls[image_url]}
                      >
                        <ScissorsIcon className="w-4"/>
                        {intl.formatMessage({id: "crop"})}
                      </Button>
                    </div>
                    <div className="flex-grow">
                      <Button
                        full
                        styleType="danger"
                        onClick={() => {
                          setImagesData(imagesData.filter(
                            ({image_url: url}) => url !== image_url
                          ))
                        }}
                      >
                        <TrashIcon className="w-4"/>
                        {intl.formatMessage({id: "delete"})}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              {/* ==================== Crop ==================== */}
              {
                image_url in crops && (
                  <div className="mt-1">
                    <div className="w-full pl-16">
                      <svg width="15" height="15" className="fill-current text-gray-100">
                        <polygon points="7.5 2.25, 15 15, 0 15" />
                      </svg>
                    </div>
                    <div className="py-3 px-4 bg-gray-100 border-4 border-t-0 rounded-2xl">
                      <p className="mb-2 flex gap-2 items-center text-base">
                        <ScissorsIcon className="w-4" />
                        <span className="font-bold">{intl.formatMessage({id: 'crop_image'})}</span>
                      </p>
                      <ImageCropper
                        intl={intl}
                        originalImage={signedUrls[image_url]}
                        mimeType={mime.lookup(image_url)}
                        cropState={crops[image_url]}
                        onCropChange={(crop) => { setCrops({...crops, [image_url]: crop}); }}
                        aspectRatio={CROP_ASPECT_RATIO}
                        imageDesc={des_image}
                        onConfirm={async (file) => { await uploadCrop(image_url, file); }}
                        onCancel={() => { setCrops(_.pick(crops, Object.keys(crops).filter((k) => k !== image_url))); }}
                        submitting={image_url in cropUploadProgresses}
                      />
                    </div>
                  </div>
                )
              }
            </div>
          ))
        }

        {/* ******************** NEW IMAGE ******************** */}
        { imagesBeingUploadedProgresses.length > 0?
        <div>
          {imagesBeingUploadedProgresses.map(({file, progress}, index) => (
            <div key={index} className="my-9">
              {/* ==================== Image ==================== */}
              <p>{file.name}</p>
              <div className={`flex items-center gap-4`}>
                <div className={`border-4 overflow-hidden w-36`} style={{aspectRatio: CROP_ASPECT_RATIO.toString()}}>
                  {/* -------------------- Preview -------------------- */}
                  <PrivateImage
                    image_url={URL.createObjectURL(file)}
                    tag="div"
                    className="w-full h-full bg-cover bg-center"
                    sign={false}
                  />
                </div>
                <div className="flex-1">
                  <ProgressBar
                    progress={progress}
                    colorBottom="bg-gray-200 animate-pulse"
                  />
                </div>
              </div>
            </div>
            ))}
        </div>:
        <div
          className={`cursor-pointer mt-2 mb-10 flex items-center justify-center border-4 w-36`}
          style={{aspectRatio: CROP_ASPECT_RATIO.toString()}}
          onClick={(e) => {
            e.preventDefault();
            hiddenImageInputRef.current.click();
          }}
        >
          {progress ? (
            <ProgressBar progress={progress} />
          ) : (
            <div className="w-10 h-10 text-gray-300">
              <PlusIcon />
            </div>
          )}
        </div>
        }
        {/* ******************** CONTROLS ******************** */}
        <Button full variant="contained" color="primary" onClick={saveImages}>
          <PhotoIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
          <FormattedMessage id="save" />
        </Button>
      </div>
    </div>
  );
};

export default WorksiteImageUploader;
